/**
 * SetupWizardPage — the operator's home page during branch onboarding (#47).
 *
 * Phase 1 scope: given a single (Zone × LC) pair, walks every onboarding
 * stage with progress + per-stage diagnostics + per-stage retry. All 6
 * stage cards compose existing surfaces — see
 * `docs/superpowers/specs/2026-05-21-setup-master-wizard-phase1.md` for
 * the full design.
 *
 * This file is the page skeleton: header + the 6 stage cards rendered
 * against `useWizardState`'s mock data. Real per-stage wiring lands in
 * follow-up commits (one per stage) so each is reviewable independently.
 */
import { useNavigate } from 'react-router-dom';
import { ExternalLink, KeyRound, Wand2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import {
  useWizardState,
  type StageKey,
  type StageState,
} from '@/hooks/useWizardState';
import SetupStageChecklist from '@/components/equipment/SetupStageChecklist';
import WgMeshStatus from '@/components/equipment/WgMeshStatus';
// KeyMismatchBanner + ResetKeysDialog removed (task #82 / xxvii): rebind
// path is now a discovery-curl rerun, not an admin action. useCurrentUser
// dropped along with them — its only consumer was the isSuperuser gate.
import { DiscoveryPanel } from '@/components/equipment/DiscoveryPanel';
import type { LocalComputerSummary } from '@/api/localComputers';
import type { ZoneDto } from '@/api/zones';
import { StageCard } from './StageCard';

const STAGE_TITLE_KEY_BY_KEY: Record<StageKey, string> = {
  jetson:         'wizard.stage.jetson.title',
  equipment:      'wizard.stage.equipment.title',
  zoning:         'wizard.stage.zoning.title',
  grafana:        'wizard.stage.grafana.title',
  notifications:  'wizard.stage.notifications.title',
  handoff:        'wizard.stage.handoff.title',
};

export default function SetupWizardPage() {
  const { t } = useT();
  const { currentZone } = useCurrentZone();
  const state = useWizardState(currentZone);
  const navigate = useNavigate();

  if (!currentZone) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <Card>
          <CardContent className="space-y-3 py-6 text-center">
            <Wand2 className="mx-auto size-8 text-muted-foreground" aria-hidden="true" />
            <h1 className="text-lg font-semibold">{t('wizard.title.empty')}</h1>
            <p className="text-sm text-muted-foreground">{t('wizard.pickBranchHint')}</p>
            <Button variant="outline" size="sm" onClick={() => navigate('/management/general')}>
              {t('wizard.openBranchPicker')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl space-y-4 px-4 py-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Wand2 className="size-5 text-primary" aria-hidden="true" />
          <h1 className="text-xl font-semibold tracking-tight">
            {t('wizard.title.withBranch', { name: currentZone.humanreadable_name })}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">{t('wizard.description')}</p>
      </header>

      <div className="space-y-3">
        {state.stages.map((stage, idx) => (
          <StageCard
            key={stage.key}
            number={idx + 1}
            title={t(STAGE_TITLE_KEY_BY_KEY[stage.key])}
            summary={stage.summary ? t(stage.summary, stage.summaryParams) : undefined}
            status={stage.status}
            blockedReasonKey={stage.blockedReason}
            errorDetail={stage.errorDetail ? t(stage.errorDetail) : undefined}
          >
            <StageBody
              stage={stage}
              lc={state.lc}
              zone={currentZone}
              refreshLc={state.refreshLc}
            />
          </StageCard>
        ))}
      </div>
    </div>
  );
}

// ── per-stage body content ─────────────────────────────────────────

function StageBody({
  stage,
  lc,
  zone,
  refreshLc,
}: {
  stage: StageState;
  lc: LocalComputerSummary | null;
  zone: ZoneDto;
  refreshLc: () => Promise<unknown>;
}) {
  if (stage.key === 'jetson') return <JetsonStageBody lc={lc} refreshLc={refreshLc} />;
  if (stage.key === 'equipment') return <EquipmentStageBody lc={lc} zone={zone} />;
  // Stages 3-6 still skeletal. Per-stage wiring lands in follow-up commits.
  return <PlaceholderBody />;
}

function PlaceholderBody() {
  const { t } = useT();
  return (
    <p className="text-xs italic text-muted-foreground">
      {t('wizard.stage.placeholderBody')}
    </p>
  );
}

/**
 * STAGE 1 body: the LC's stage checklist (re-using SetupStageChecklist —
 * same component the admin LC modal renders) + CTAs.
 *
 * Two shapes:
 *   - LC bound: show checklist + "Open admin LC detail" deep-link.
 *   - No LC: "factory-fresh" entry shape (a) — show "Issue first curl
 *     token" CTA that deep-links to /admin/local-computers. After
 *     #53 + #52 ship (device_number + wg_ip FE fields) we'll surface
 *     the issue-token form inline so the operator doesn't leave the page.
 */
function JetsonStageBody({
  lc,
  refreshLc: _refreshLc,
}: {
  lc: LocalComputerSummary | null;
  refreshLc: () => Promise<unknown>;
}) {
  const { t } = useT();
  const navigate = useNavigate();

  if (!lc) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">{t('wizard.stage.jetson.noLcHint')}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/admin/local-computers')}
        >
          <KeyRound className="size-3.5" />
          {t('wizard.stage.jetson.issueToken')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{lc.display_name}</span>
        <span className="ml-2 font-mono">{lc.hostname}</span>
      </div>
      {/* KeyMismatchBanner removed (xxvii / task #82): rebind is a
          discovery-curl rerun, surfaced from the LC detail page's
          persistent Discovery panel — no separate banner. */}
      <SetupStageChecklist
        lc={lc}
        lastSetupError={lc.last_setup_error}
      />
      {/* Live WireGuard mesh state — same component the admin LC modal
          renders. Shows the operator at a glance whether the peer is
          in the mesh and whether wg-quick on the jetson has handshaken.
          `onShowCurl` deep-links to the LC detail page where the
          UnconnectedActions curl block sits at the top. */}
      <WgMeshStatus
        lc={lc}
        onShowCurl={() => navigate(`/admin/local-computers?lcId=${lc.id}`)}
      />
      {/* ResetKeysDialog mount removed (xxvii / task #82). */}
      <Button
        size="sm"
        variant="outline"
        onClick={() => navigate(`/admin/local-computers?lcId=${lc.id}`)}
      >
        <ExternalLink className="size-3.5" />
        {t('wizard.stage.jetson.openLcDetail')}
      </Button>
    </div>
  );
}

/**
 * STAGE 2 body: embeds the existing `<DiscoveryPanel>` (master autodiscovery
 * flow — Router → NVR → cameras with credential-retry pauses, design doc
 * §2.2). Renders nothing extra when the LC isn't DOCKER_RUNNING — the
 * card's status icon + blockedReason already make the gate visible.
 *
 * DiscoveryPanel manages its own URL-hash runId, polling, cancel,
 * credential-prompt sub-flow — the wizard just gives it a slot. Same
 * panel that lives in the equipment tab; this is reuse-first, not a fork.
 */
function EquipmentStageBody({
  lc,
  zone,
}: {
  lc: LocalComputerSummary | null;
  zone: ZoneDto;
}) {
  const { t } = useT();
  const navigate = useNavigate();

  if (!lc || lc.setup_stage !== 'DOCKER_RUNNING') {
    // Status icon + blocked banner already cover the gate; no body content.
    return null;
  }

  return (
    <div className="space-y-3">
      <DiscoveryPanel lcId={lc.id} zoneId={zone.id} />
      <Button
        size="sm"
        variant="outline"
        onClick={() => navigate('/management/equipment')}
      >
        <ExternalLink className="size-3.5" />
        {t('wizard.stage.equipment.openTab')}
      </Button>
    </div>
  );
}
